import React, { Fragment, FunctionComponent } from 'react';
import { Typography, Grid, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Tooltip, Paper } from '@material-ui/core';
import styles from "./PtScorecard.Styles";
import ScorecardSectionDataModel from '../../../../../data-models/pt/ScorecardSection.DataModel';
import Moment from 'moment';
import MedicalServiceDataModel from "../../../../../data-models/MedicalService.DataModel";

interface IColumnConfiguration {
    id: string,
    label: string,
    minWidth: number,
    align?: 'inherit' | 'left' | 'center' | 'right' | 'justify',
    format?: (value) => string,
    color?: (value: number) => string
}

interface IPtScorecardProps {
    medicalService: MedicalServiceDataModel,
    scorecardSections: Array<ScorecardSectionDataModel>
}

export const PtScorecard: FunctionComponent<IPtScorecardProps> = (props: IPtScorecardProps) => {
    const classes = styles();

    const columns: Array<IColumnConfiguration> = [
        {
            id: 'score',
            label: 'Score Reported',
            minWidth: 170,
            align: 'left'
        },
        {
            id: 'date',
            label: 'Date Reported',
            minWidth: 170,
            align: 'left',
            format: (value) => Moment(value).format('MM/DD/YYYY'),
        },
        {
            id: 'total',
            label: 'Total Score',
            minWidth: 170,
            align: 'left',
            format: (value) => value.toFixed(1),
            color: (value) => value >= 1.4 ? '#960018' : value >= 1 ? '#FFD700' : '#018749'
        },
        {
            id: 'visits',
            label: 'Total Visits Attended',
            minWidth: 170,
            align: 'left',
        }
    ];

    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <Paper className={classes.paper}>
                    <Grid container spacing={3} className={classes.gridContainer}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" className={classes.blackFont}>The physician's examination of the claimant and the resulting report reflects the following scorecard</Typography>
                        </Grid>
                        <Grid item xs={8}>
                            <Typography variant="subtitle2" className={classes.paperTitle}>Claim Progress</Typography>

                            <TableContainer className={classes.tableContainer}>
                                <Table stickyHeader aria-label="sticky table">
                                    <TableHead>
                                        <TableRow>
                                            {columns.map((column) => (
                                                <TableCell
                                                    key={column.id}
                                                    align={column.align}
                                                    style={{ minWidth: column.minWidth }}
                                                >
                                                    {column.label}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {props.medicalService?.scorecardResponsesChartContent?.map(row => {
                                            return (
                                                <TableRow hover role="checkbox" tabIndex={-1} key={row.score}>
                                                    {columns.map((column) => {
                                                        const value = row[column.id];
                                                        const tooltipValue: string = props.scorecardSections.map(scorecardSection => {
                                                            return scorecardSection.description + ": " + (row.scorecardResponses.filter(x => x.scorecardSectionId == scorecardSection.scorecardSectionId)[0]?.value ? row.scorecardResponses.filter(x => x.scorecardSectionId == scorecardSection.scorecardSectionId)[0]?.value : scorecardSection.options?.filter(option => option.scorecardOptionId == row.scorecardResponses.filter(x => x.scorecardSectionId == scorecardSection.scorecardSectionId)[0]?.scorecardOptionId)[0]?.value ? scorecardSection.options?.filter(option => option.scorecardOptionId == row.scorecardResponses.filter(x => x.scorecardSectionId == scorecardSection.scorecardSectionId)[0]?.scorecardOptionId)[0]?.value : 'NA')
                                                        }).join(' - ');
                                                        return (
                                                            <Tooltip title={
                                                                props.scorecardSections.map(scorecardSection => {
                                                                    return (<Fragment>
                                                                        <Typography variant="subtitle2">*{scorecardSection.description + ": " + (row.scorecardResponses.filter(x => x.scorecardSectionId == scorecardSection.scorecardSectionId)[0]?.value ? row.scorecardResponses.filter(x => x.scorecardSectionId == scorecardSection.scorecardSectionId)[0]?.value : scorecardSection.options?.filter(option => option.scorecardOptionId == row.scorecardResponses.filter(x => x.scorecardSectionId == scorecardSection.scorecardSectionId)[0]?.scorecardOptionId)[0]?.value ? scorecardSection.options?.filter(option => option.scorecardOptionId == row.scorecardResponses.filter(x => x.scorecardSectionId == scorecardSection.scorecardSectionId)[0]?.scorecardOptionId)[0]?.value : 'NA')}</Typography>
                                                                    </Fragment>)
                                                                })
                                                            }>
                                                                <TableCell key={column.id} align={column.align} style={column.id === 'total' ? { backgroundColor: column.color(value) } : {}}>
                                                                    {column.format ? column.format(value) : value}
                                                                </TableCell>
                                                            </Tooltip>
                                                        );
                                                    })}
                                                </TableRow>
                                            );
                                        }).reverse()}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>
                        <Grid item xs={4}>
                            <Typography variant="subtitle2" className={classes.paperTitle}>Latest Score Details: {props.medicalService?.scorecardResponsesChartContent.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0]?.score}</Typography>
                            {
                                props.scorecardSections?.map(scorecardSection => (
                                    <Grid container spacing={3} className={classes.gridContainer}>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2" className={classes.blackFont}>{scorecardSection.description}: {props.medicalService?.scorecardResponsesChartContent[0]?.scorecardResponses.filter(x => x.scorecardSectionId == scorecardSection.scorecardSectionId)[0]?.value ? props.medicalService?.scorecardResponsesChartContent[0]?.scorecardResponses.filter(x => x.scorecardSectionId == scorecardSection.scorecardSectionId)[0]?.value : scorecardSection.options?.filter(option => option.scorecardOptionId == props.medicalService?.scorecardResponsesChartContent[0]?.scorecardResponses.filter(x => x.scorecardSectionId == scorecardSection.scorecardSectionId)[0]?.scorecardOptionId)[0]?.value ? scorecardSection.options?.filter(option => option.scorecardOptionId == props.medicalService?.scorecardResponsesChartContent[0]?.scorecardResponses.filter(x => x.scorecardSectionId == scorecardSection.scorecardSectionId)[0]?.scorecardOptionId)[0]?.value : '-'}</Typography>
                                        </Grid>
                                    </Grid>
                                ))
                            }
                        </Grid>
                        <Grid item xs={4}>
                            <Typography variant="subtitle2" className={classes.greenProgress}>Claim progressing as expected</Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Typography variant="subtitle2" className={classes.yellowProgress}>Claim has potential delays in recovery</Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Typography variant="subtitle2" className={classes.redProgress}>Claim is not meeting expectations on recovery</Typography>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>
        </Grid>
    );
}